/* eslint-disable max-len */
import React, { useEffect } from "react";
import { WELLNESS_RENEWAL_ENTRY_POINTS, modalStorageKeys } from "../../../config/params";
import { useCombinedAlertsDataStore, useWellnessRenewalFunnel } from "../../../hooks";

import AvatarCustom from "../../../components/AvatarCustom/AvatarCustom";
import { CloseIcon } from "../../../components/Icons";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { StyledBookingFlowButton } from "../../containers/BookingScheduleContainer/BookingScheduleContainer.style";
import WellnessRenewalPetAvatars from "../../../components/PetAvatars/WellnessRenewalPetAvatars";
import { markPetsShownForModal } from "../../../services/wellness-plans-by-user/wellness-plan-by-user.service";
import { useModal } from "../../../context/ModalContext";
import { userActivityLogger } from "../../../utils/mixpanel";

const SINGLE_PET_BULLETS = [
  "Monthly Payments: Cover your pet's annual care with 12 monthly payments.",
  "TotalCare + Surgery: add one dental, spay, or neuter procedure for up to 25% savings.",
];

const WellnessRenewalUpsellModal = () => {
  const { closeModal } = useModal();
  const { endsSoonWPData } = useCombinedAlertsDataStore();
  const { startRenewalFunnel } = useWellnessRenewalFunnel();

  const petData = endsSoonWPData;
  const storageKey = modalStorageKeys.wellnessPlansEndingSoon;
  const isMultiPet = petData.length > 1;

  const handleReviewPlans = () => {
    if (petData?.length) {
      markPetsShownForModal(
        storageKey,
        petData.map((p) => p.petId),
      );
    }

    startRenewalFunnel(petData, WELLNESS_RENEWAL_ENTRY_POINTS.RENEWAL_UPSELL_MODAL);
  };

  const handleClose = () => {
    if (petData?.length) {
      markPetsShownForModal(
        storageKey,
        petData.map((p) => p.petId),
      );
    }

    petData.forEach((pet) => {
      userActivityLogger(MIXPANEL_EVENTS.WP_VALUE_POP_UP_DISMISSED, {
        pet_name: pet.petName,
        plan_name: pet.wellnessPlanName,
      });
    });

    closeModal();
  };

  useEffect(() => {
    userActivityLogger(MIXPANEL_EVENTS.UPSELL_RENEWAL_MODAL_SHOWN, {
      pet_name: petData?.length > 0 ? petData[0].petName : "",
      plan_name: petData?.length > 0 ? petData[0].wellnessPlanName : "",
      number_of_pets: petData?.length || 0,
    });
  }, []);

  return (
    <div className="flex flex-col gap-4 w-full xl:w-[25rem]">
      {/* Top Nav with Close Button */}
      <div className="flex items-end justify-end w-full">
        <div className="w-8 h-8 bg-stone-50 rounded-full flex items-center justify-center cursor-pointer">
          <CloseIcon onClick={handleClose} />
        </div>
      </div>

      {isMultiPet ? (
        <>
          {/* Text Content */}
          <div className="flex flex-col gap-3 items-center w-full">
            <div className="font-semibold text-2xl leading-7 text-center text-light-black w-full">
              <h3>TotalCare Plan Renewal</h3>
            </div>

            <div className="text-base leading-6 text-center text-light-black w-full">
              <p>
                TotalCare options might look a little different this year. Keep their care plan on
                track by reviewing your options today.
              </p>
            </div>
          </div>

          {/* Per-pet cards */}
          <div className="flex flex-col gap-2 w-full max-h-[18rem] overflow-y-auto scrollbar-thin pe-1">
            {petData.map((pet, index) => (
              <div
                key={index}
                className="flex gap-3 items-center bg-white rounded-xl p-4 w-full"
              >
                <div className="shrink-0">
                  <AvatarCustom
                    width={56}
                    height={56}
                    image={pet?.petImage ?? undefined}
                    name={pet.petName}
                    isWellnessMember={true}
                  />
                </div>
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="font-semibold text-base text-light-black capitalize">
                    {`${pet.petName}'s Plan`}
                  </div>
                  <div className="text-sm leading-5 text-light-black">
                    {`${pet.planName} will not renew at the end of your term.`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Pet Avatar */}
          <div className="flex justify-center w-full">
            <WellnessRenewalPetAvatars
              petDetails={petData}
              defaultWellnessStatus={true}
            />
          </div>

          {/* Text Content */}
          <div className="flex flex-col gap-3 items-center w-full">
            <div className="font-semibold text-2xl leading-7 text-center text-light-black w-full">
              <h3>It&apos;s Time to Renew TotalCare</h3>
            </div>

            <div className="text-base leading-6 text-center text-light-black w-full">
              <p>
                {`${petData?.length > 0 ? petData[0].petName : "Your pet"}'s TotalCare options might look a little different this year. Please take a moment to see what's new:`}
              </p>
            </div>

            <ul className="flex flex-col gap-2 w-full list-disc ps-5 text-sm leading-5 text-light-black">
              {SINGLE_PET_BULLETS.map((bullet, index) => (
                <li key={index}>{bullet}</li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* Button */}
      <div className="flex flex-col items-center justify-center w-full">
        <StyledBookingFlowButton
          disabled={false}
          fullWidth
          color="primary"
          size="small"
          onClick={handleReviewPlans}
        >
          See Your Options
        </StyledBookingFlowButton>
      </div>
    </div>
  );
};

export default WellnessRenewalUpsellModal;
